/**
 * Calendário e horários do agendamento (antes no AppointmentService: getAvailableDays,
 * generateMonth e getAvailableTimes). Puras: recebem o "agora" para poder testar.
 */
import moment, { type Moment } from "moment";

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/** Resposta de availableDays ([{ weekday: "monday" }, ...]) → índices do moment (0 = domingo). */
export const toWeekdayIndexes = (days: { weekday: string }[]) =>
  days.map((d) => WEEKDAYS.indexOf(d.weekday)).filter((i) => i >= 0);

export type CalendarDay = {
  date: Moment;
  currentDate: Moment;
  formattedDate: string;
  day: string;
  isToday: boolean;
  disabled: boolean;
};
export type CalendarMonth = { date: Moment; firstAvailableDay: string | null; month: CalendarDay[][] };

/** Grade de 6 semanas do mês de `date`. Passado, hoje, outro mês e dia sem agenda ficam desabilitados. */
export function generateMonth(availableWeekDays: number[], date: Moment, now: Moment = moment()): CalendarMonth {
  const dayIsDisabled = (day: Moment) =>
    day.isSameOrBefore(now, "day") || !availableWeekDays.includes(moment(day).weekday());
  let firstAvailableDay: string | null = null;
  const today = now.format("YYYY-MM-DD");
  const currDay = date.clone().startOf("month").startOf("week");
  const month: CalendarDay[][] = [[], [], [], [], [], []];
  for (let i = 0; i < 6; i++) {
    for (let j = 0; j <= 6; j++) {
      const formattedDate = currDay.format("YYYY-MM-DD");
      const disabled = dayIsDisabled(currDay) || date.format("YYYY-MM") != currDay.format("YYYY-MM");
      if (!firstAvailableDay && !disabled) firstAvailableDay = formattedDate;
      month[i].push({
        date: currDay.clone(),
        currentDate: moment(formattedDate),
        formattedDate,
        day: currDay.format("D"),
        isToday: today == formattedDate,
        disabled,
      });
      currDay.add(1, "days");
    }
  }
  return { date, firstAvailableDay, month };
}

/** Os 6 meses do calendário, a partir de amanhã. */
export function buildMonths(availableWeekDays: number[], now: Moment = moment(), monthsQtd = 6) {
  const firstMonth = now.clone().add(1, "day");
  return Array.from({ length: monthsQtd }, (_, i) =>
    generateMonth(availableWeekDays, firstMonth.clone().add(i, "month"), now),
  );
}

/** Horários do dia: só os futuros, com o texto "sexta-feira das 10h00 às 10h50" (o legado escrevia "ás"). */
export function formatAvailableTimes(times: string[], now: Moment = moment()) {
  return times
    .filter((t) => moment(t).isAfter(now))
    .map((date) => {
      const st = moment(date);
      return {
        date,
        formattedDate: `${st.format("dddd")} das ${st.format("HH[h]mm")} às ${st.add(50, "minutes").format("HH[h]mm")}`,
      };
    });
}
